/**
 * Componente para visualizar la ruta de trazabilidad en el mapa
 * @module components/map/TracePath
 */

import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { useFiberStore, useNodeStore } from '@/stores'
import { describeFiberIdForUi } from '@/utils/fiberIdDisplay'
import { cableLabelShort } from '@/utils/cableDisplayLabel'
import type { Cable } from '@/types/fibers'

interface TracePathProps {
  map: L.Map
}

export function TracePath({ map }: TracePathProps) {
  const activeTrace = useFiberStore((state) => state.activeTrace)
  const cables = useFiberStore((state) => state.cables)
  const nodes = useNodeStore((state) => state.nodes)
  const layerGroupRef = useRef<L.LayerGroup | null>(null)
  const highlightMarkersRef = useRef<L.CircleMarker[]>([])

  useEffect(() => {
    if (!map) return

    // Crear o limpiar layer group
    if (!layerGroupRef.current) {
      layerGroupRef.current = L.layerGroup().addTo(map)
    } else {
      layerGroupRef.current.clearLayers()
    }

    // Limpiar marcadores de highlight
    highlightMarkersRef.current.forEach(marker => marker.remove())
    highlightMarkersRef.current = []

    // Si no hay trace activo o no fue exitoso, no renderizar nada
    if (!activeTrace || !activeTrace.success) {
      return
    }

    // Crear un mapa de nodos por ID
    const nodeMap = new Map(nodes.map((node) => [node.id, node]))

    // Obtener todos los nodos en la ruta
    const routeNodeIds = activeTrace.path.map(segment => segment.nodeId)
    const routeNodes = routeNodeIds
      .map(id => nodeMap.get(id))
      .filter((node): node is NonNullable<typeof node> => node !== undefined)

    if (routeNodes.length < 2) {
      console.warn('No hay suficientes nodos para renderizar la ruta')
      return
    }

    // Crear polyline principal con la ruta completa
    const routeCoordinates = routeNodes.map(node => [node.lat, node.lng] as [number, number])

    const mainPolyline = L.polyline(routeCoordinates, {
      color: '#eab308', // Amarillo
      weight: 6,
      opacity: 0.8,
      dashArray: '10, 5',
      className: 'trace-path-line',
    })

    // Animación de línea (efecto punteado que se mueve)
    let dashOffset = 0
    const animateInterval = setInterval(() => {
      dashOffset -= 1
      mainPolyline.setStyle({ dashOffset: String(dashOffset) })
    }, 50)

    mainPolyline.addTo(layerGroupRef.current!)

    const fibraLegible = describeFiberIdForUi(activeTrace.fiberId, cables).primary

    mainPolyline.bindTooltip(
      `
        <div class="text-xs">
          <strong>Ruta activa</strong><br/>
          ${fibraLegible}<br/>
          Distancia: ${activeTrace.totalDistanceKm.toFixed(2)} km<br/>
          Saltos: ${activeTrace.path.length}
        </div>
      `,
      { sticky: true }
    )

    // Crear marcadores de highlight para cada nodo en la ruta
    routeNodes.forEach((node, index) => {
      const isFirst = index === 0
      const isLast = index === routeNodes.length - 1

      // Marcador de highlight (círculo amarillo grande)
      const highlightMarker = L.circleMarker([node.lat, node.lng], {
        radius: 15,
        fillColor: isFirst ? '#10b981' : isLast ? '#ef4444' : '#eab308', // Verde inicio, rojo fin, amarillo intermedio
        color: '#ffffff',
        weight: 3,
        opacity: 1,
        fillOpacity: 0.5,
      })

      // Animación de pulso
      let growing = true
      let currentRadius = 15
      const pulseInterval = setInterval(() => {
        if (growing) {
          currentRadius += 0.5
          if (currentRadius >= 20) growing = false
        } else {
          currentRadius -= 0.5
          if (currentRadius <= 15) growing = true
        }
        highlightMarker.setRadius(currentRadius)
      }, 100)

      const segment = activeTrace.path[index]
      const refC = (segment as { nodeRefCode?: string })?.nodeRefCode || (node as { refCode?: string }).refCode
      const nom = (node as { nombre?: string }).nombre
      const nodeTitle = [refC, nom].filter(Boolean).join(' · ') || node.id
      const cab = segment?.cableId ? cables.find((c) => c.id === segment.cableId) : undefined
      let cableTxt = '—'
      if (segment?.cableId) {
        if (cab) cableTxt = cableLabelShort(cab as Cable)
        else {
          const bits = [
            (segment as { cableCodigo?: string }).cableCodigo &&
              `[${(segment as { cableCodigo?: string }).cableCodigo}]`,
            (segment as { cableNombre?: string }).cableNombre,
          ].filter(Boolean) as string[]
          cableTxt =
            bits.length > 0 ? bits.join(' ') : `${segment.cableId.slice(0, 8)}…`
        }
      }
      const popupContent = `
        <div class="p-2 text-xs">
          <div class="font-bold mb-1">${nodeTitle}</div>
          <div class="text-gray-600">${node.tipo}</div>
          <div class="text-gray-600">
            ${isFirst ? '🟢 Inicio de ruta' : isLast ? '🔴 Fin de ruta' : `📍 Punto ${index + 1}`}
          </div>
          ${
            segment
              ? `
            <div class="mt-2 text-xs border-t pt-2">
              <div>Cable: ${cableTxt}</div>
              <div>Tramo: ${segment.distanceKm.toFixed(2)} km</div>
              ${segment.hasSplice ? '<div class="text-orange-600">⚡ Empalme</div>' : ''}
            </div>
          `
              : ''
          }
        </div>
      `

      highlightMarker.bindPopup(popupContent, { maxWidth: 250 })

      highlightMarker.addTo(layerGroupRef.current!)
      highlightMarkersRef.current.push(highlightMarker)

      // Cleanup del intervalo cuando el marker sea removido
      highlightMarker.on('remove', () => {
        clearInterval(pulseInterval)
      })
    })

    // Ajustar vista del mapa para mostrar toda la ruta
    if (routeCoordinates.length > 0) {
      const bounds = L.latLngBounds(routeCoordinates)
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 16,
        animate: true,
        duration: 1,
      })
    }

    // Cleanup
    return () => {
      clearInterval(animateInterval)
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers()
      }
      highlightMarkersRef.current.forEach(marker => marker.remove())
      highlightMarkersRef.current = []
    }
  }, [map, activeTrace, nodes, cables])

  return null // Este componente no renderiza nada directamente
}
